import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Archive, RotateCcw, Trash2, Loader2, Camera } from "lucide-react";

/**
 * Catalog snapshots UI — lets the owner archive the current products.json,
 * list past snapshots, restore one (with an auto-safety-snapshot taken first),
 * or delete an old snapshot.
 */
export default function SnapshotsPanel() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(null); // "create" | "restore:<name>" | "delete:<name>"
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products/snapshots", { withCredentials: true });
      setSnapshots(data.snapshots || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load snapshots.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    setError(null);
    try {
      await api.post(
        "/admin/products/snapshots",
        { label: label.trim() || null },
        { withCredentials: true }
      );
      setLabel("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create snapshot.");
    } finally {
      setBusy(null);
    }
  };

  const restore = async (name) => {
    if (!window.confirm(
      `Restore catalog from "${name}"?\n\nCurrent products will be replaced. A safety snapshot of the current state is taken automatically before the restore.`
    )) return;
    setBusy(`restore:${name}`);
    setError(null);
    try {
      await api.post(
        `/admin/products/snapshots/${encodeURIComponent(name)}/restore`,
        {},
        { withCredentials: true }
      );
      await load();
      // Tell the parent (ProductManager) to reload its list
      window.dispatchEvent(new CustomEvent("bb:products-reload"));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to restore snapshot.");
    } finally {
      setBusy(null);
    }
  };

  const del = async (name) => {
    if (!window.confirm(`Delete snapshot "${name}"? This cannot be undone.`)) return;
    setBusy(`delete:${name}`);
    setError(null);
    try {
      await api.delete(
        `/admin/products/snapshots/${encodeURIComponent(name)}`,
        { withCredentials: true }
      );
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete snapshot.");
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <section
      data-testid="snapshots-panel"
      className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <Archive size={16} className="text-[#A855F7]" />
        <h3 className="font-display text-lg text-white">Catalog snapshots</h3>
        <span className="text-white/40 text-xs ml-1">
          — safety net before big edits
        </span>
      </div>

      <form
        onSubmit={create}
        className="flex flex-col sm:flex-row gap-2 mb-4"
        data-testid="snapshot-create-form"
      >
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Optional label (e.g. before-summer-sale)"
          data-testid="snapshot-label-input"
          maxLength={40}
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF3D8B]"
        />
        <button
          type="submit"
          disabled={busy === "create"}
          data-testid="snapshot-create-btn"
          className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] disabled:opacity-40 transition-colors"
        >
          {busy === "create" ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          Save snapshot
        </button>
      </form>

      {error && (
        <div className="text-red-400 text-xs mb-3" data-testid="snapshot-error">{error}</div>
      )}

      {loading ? (
        <div className="text-white/50 text-sm">Loading snapshots…</div>
      ) : snapshots.length === 0 ? (
        <div className="text-white/50 text-xs italic">
          No snapshots yet. Save one before your next big catalog change.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="snapshots-list">
          {snapshots.map((s) => (
            <li
              key={s.name}
              data-testid={`snapshot-row-${s.name}`}
              className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-black/30"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-semibold truncate">
                  {s.label ? s.label : "(unlabeled)"}
                  {s.label === "auto-before-restore" && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-[#A855F7]">
                      auto
                    </span>
                  )}
                </div>
                <div className="text-white/50 text-xs mt-0.5">
                  {fmtDate(s.created_at)} · {s.product_count} products
                </div>
              </div>
              <button
                onClick={() => restore(s.name)}
                disabled={busy === `restore:${s.name}`}
                data-testid={`snapshot-restore-${s.name}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#A855F7]/40 text-[#A855F7] text-[11px] uppercase tracking-widest hover:bg-[#A855F7]/10 disabled:opacity-40 transition-colors"
              >
                {busy === `restore:${s.name}`
                  ? <Loader2 size={12} className="animate-spin" />
                  : <RotateCcw size={12} />}
                Restore
              </button>
              <button
                onClick={() => del(s.name)}
                disabled={busy === `delete:${s.name}`}
                data-testid={`snapshot-delete-${s.name}`}
                className="p-1.5 rounded-full text-white/40 hover:text-red-400 disabled:opacity-40 transition-colors"
                aria-label={`Delete snapshot ${s.name}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
